import { SuburbPlace } from '../../../../services/locations.service';
import { MapPlace, ScreenPoint } from '../map/map-types';
import { kindLabel } from './place-kinds';

/**
 * The selected suburb's places as the Places tab and the map show them (#253 step 3): where each stands (its marker),
 * the type menu, the filters, and where a marker's popup sits. Pure, tested in place-list.spec.ts.
 */

export interface KindOption {
  label: string;
  value: string | null;
}

/** Where the popup over a marker sits: x kept inside the visible map; shift is how far that moved it off the marker. */
export interface PopupPosition {
  x: number;
  y: number;
  /** Below the marker, when the marker is too near the top for the popup to fit above it. */
  below: boolean;
  shift: number;
}

/** The popup is 17rem wide (half of it, in px) and about 21rem tall; it keeps this far from the map's edges. */
const POPUP_HALF_WIDTH = 136;
const POPUP_EDGE = 8;
const POPUP_HEIGHT = 340;

/** A place's marker: in Conductor, on the website, both, waiting for review, hidden, or none of those. */
export function placeMarkerState(p: SuburbPlace): MapPlace['state'] {
  if (p.provider) return p.listing === 'listed' ? 'both' : 'conductor';
  if (p.listing === 'listed') return 'website';
  if (p.listing === 'pending') return 'pending';
  return p.status === 'hidden' ? 'hidden' : 'other';
}

export function toMapPlace(p: SuburbPlace): MapPlace {
  return { id: p.locationId, name: p.name, lat: p.lat, lng: p.lng, state: placeMarkerState(p) };
}

/** The type menu: every type in the suburb, most common first (then by name), with counts. */
export function kindOptions(places: SuburbPlace[]): KindOption[] {
  const n = new Map<string, number>();
  for (const p of places) n.set(p.kind, (n.get(p.kind) ?? 0) + 1);
  const kinds = [...n].sort(
    (a, b) => b[1] - a[1] || kindLabel(a[0]).localeCompare(kindLabel(b[0]))
  );
  return [
    { label: `All types (${places.length})`, value: null },
    ...kinds.map(([k, c]) => ({ label: `${kindLabel(k)} (${c})`, value: k })),
  ];
}

/** The places of one type (null: every type) whose name or type contains the text. */
export function filterPlaces(
  places: SuburbPlace[],
  kind: string | null,
  text: string
): SuburbPlace[] {
  const q = text.trim().toLowerCase();
  const matches = (p: SuburbPlace) =>
    !q || p.name.toLowerCase().includes(q) || kindLabel(p.kind).toLowerCase().includes(q);
  return places.filter((p) => (!kind || p.kind === kind) && matches(p));
}

/**
 * Where the popup goes for a marker at `pt`: above it, or below it when it is near the top; kept clear of the map's
 * edges and of the side panel (`visibleWidth` is the map's width less the panel's) where the map is wide enough.
 */
export function popupPosition(pt: ScreenPoint, visibleWidth: number): PopupPosition {
  const min = POPUP_HALF_WIDTH + POPUP_EDGE;
  const x = Math.min(
    Math.max(pt.x, min),
    Math.max(min, visibleWidth - POPUP_HALF_WIDTH - POPUP_EDGE)
  );
  return { x, y: pt.y, below: pt.y < POPUP_HEIGHT, shift: x - pt.x };
}
